import React, { useState, useMemo, useRef, useEffect, useContext } from "react";
import {
    printReports,
    getSignalColorFromDevice,
    getColorVariant,
    getSignalTypeFromBits,
} from "../../utils";
import { detectTransitions } from "../../utils/productionCalculations";
import { useProductionData } from "../../hooks/useProductionData";
import StarekContext from "../../context/StarekContext";
import ReportResults from "./ReportResults";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell,
} from "recharts";

export default function ProductionReport({
    filters,
    range,
    deviceData,
    onRefresh,
    onFavoriteClick,
    onDownloadCSV,
    showAssignJobLink = true,
    hideControls = false,
    cachedDatapoints,
    isLoadingDatapoints,
    selectedDeviceIndex,
    onSelectedDeviceIndexChange,
    showThresholds = true,
    onShowThresholdsChange,
}) {
    const [loading, setLoading] = useState(false);
    const [aiAnalyzing, setAiAnalyzing] = useState(false);
    const [aiResults, setAiResults] = useState(null);
    const [aiError, setAiError] = useState(null);
    const [showAiPanel, setShowAiPanel] = useState(false);
    const [hideEmptyDevices, setHideEmptyDevices] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [autoRefreshIntervalId, setAutoRefreshIntervalId] = useState(null);
    const [localShowThresholds, setLocalShowThresholds] =
        useState(showThresholds);
    const printRef = useRef(null);
    const starekContext = useContext(StarekContext);
    const { auth, lastUsedWorkspace } = starekContext;
    const types = starekContext.types;

    // Calculate time range for production data
    const timeRange = useMemo(() => {
        if (!filters?.durationStart || !filters?.durationEnd) {
            return null;
        }

        let start = filters.durationStart;
        let end = filters.durationEnd;

        if (Array.isArray(range) && range.length === 2) {
            const base = new Date(filters.durationStart);
            if (!Number.isNaN(base.getTime())) {
                const [startOffsetSec, endOffsetSec] = range;
                start = new Date(
                    base.getTime() + startOffsetSec * 1000,
                ).toISOString();
                end = new Date(
                    base.getTime() + endOffsetSec * 1000,
                ).toISOString();
            }
        }

        return { start, end };
    }, [filters, range]);

    // Filter devices based on selection
    const allShadowDevices = deviceData?.shadowDevices || [];
    const shadowDevices = useMemo(() => {
        if (!allShadowDevices || allShadowDevices.length === 0) return [];
        if (!filters?.devices) return allShadowDevices;

        const selectedDeviceNames = filters.devices
            .split(",")
            .map((d) => d.trim())
            .filter(Boolean);

        if (selectedDeviceNames.length === 0) return allShadowDevices;

        return allShadowDevices.filter((dev) => {
            const deviceName = dev.name || dev.id;
            return selectedDeviceNames.includes(deviceName);
        });
    }, [allShadowDevices, filters?.devices]);

    const {
        data: productionDevices,
        loading: productionLoading,
        refetch,
    } = useProductionData(
        shadowDevices,
        timeRange,
        cachedDatapoints,
        isLoadingDatapoints,
    );

    const handleDownloadCSV = () => {
        if (onDownloadCSV) onDownloadCSV();
    };

    const handleRefresh = async () => {
        setLoading(true);
        try {
            await Promise.all([onRefresh?.(), refetch()]);
        } catch (error) {
            console.error("Error refreshing data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyzeWithAI = async () => {
        try {
            setAiAnalyzing(true);
            setAiError(null);
            setShowAiPanel(true);

            const { scheme, host, port } = lastUsedWorkspace.engine;
            const workspaceId = lastUsedWorkspace.name;

            const url = `${scheme}://${host}:${port}/api/v1/${workspaceId}/datapoints/ai-summary`;

            const payload = {
                input: "Analyze the device production data and provide insights on trends, anomalies, and recommendations",
                data: productionDevices,
            };

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();
            console.log("AI Analysis Result:", result);
            setAiResults(result);
        } catch (error) {
            console.error("Error analyzing with AI:", error);
            setAiError("Failed to analyze data with AI. Please try again.");
            setAiResults(null);
        } finally {
            setAiAnalyzing(false);
        }
    };

    useEffect(() => {
        setLoading(productionLoading);
    }, [productionLoading]);

    // Setup auto-refresh interval
    useEffect(() => {
        if (autoRefresh && refetch) {
            const intervalId = setInterval(() => {
                refetch().catch((err) =>
                    console.error("Auto-refresh failed:", err),
                );
            }, 60000); // 60 seconds
            setAutoRefreshIntervalId(intervalId);

            return () => {
                if (intervalId) clearInterval(intervalId);
            };
        } else {
            if (autoRefreshIntervalId) {
                clearInterval(autoRefreshIntervalId);
                setAutoRefreshIntervalId(null);
            }
        }
    }, [autoRefresh, refetch]);

    const allDevices = useMemo(() => {
        if (!shadowDevices || shadowDevices.length === 0) return [];
        if (!filters?.devices) return shadowDevices.map((d) => d.name || d.id);

        const selectedDeviceNames = filters.devices
            .split(",")
            .map((d) => d.trim())
            .filter(Boolean);

        return shadowDevices
            .filter((dev) => {
                const deviceName = dev.name || dev.id;
                return selectedDeviceNames.includes(deviceName);
            })
            .map((d) => d.name || d.id);
    }, [shadowDevices, filters]);

    const validatedDeviceIndex = useMemo(() => {
        if (allDevices.length === 0) return 0;
        return Math.min(selectedDeviceIndex, allDevices.length - 1);
    }, [selectedDeviceIndex, allDevices]);

    const deviceList = useMemo(() => {
        if (!hideEmptyDevices) return allDevices;
        return allDevices.filter((deviceName) => {
            const matchingDev = productionDevices.find(
                (d) => (d.name || d.id) === deviceName,
            );
            return matchingDev && matchingDev.totalUnitsProduced > 0;
        });
    }, [allDevices, productionDevices, hideEmptyDevices]);

    const currentDeviceName = deviceList[validatedDeviceIndex];

    // Get current device from productionDevices
    const currentDevice = useMemo(() => {
        if (!currentDeviceName || !productionDevices) return null;
        return productionDevices.find(
            (dev) => (dev.name || dev.id) === currentDeviceName,
        );
    }, [currentDeviceName, productionDevices]);

    // Prepare chart data for current device only
    const chartData = useMemo(() => {
        try {
            console.log(
                "ProductionReport chartData - currentDevice exists:",
                !!currentDevice,
                "currentDevice name:",
                currentDevice?.name || currentDevice?.id,
            );
            console.log(
                "ProductionReport chartData - cachedDatapoints structure:",
                cachedDatapoints ? Object.keys(cachedDatapoints) : "null",
            );

            if (!currentDevice) return { data: [], colors: [] };

            const data = [];
            const colors = [];

            const runtimeSignals = currentDevice.device?.runtime?.signals || [];
            const signalsToProcess = Array.isArray(runtimeSignals)
                ? runtimeSignals.filter((s) => s != null)
                : [];
            const defaultSignalColors =
                types?.device?.defaultSignalColors || [];

            // Parse device parameters
            let params = currentDevice.parameters;
            if (typeof params === "string") {
                try {
                    params = JSON.parse(params);
                } catch (e) {
                    params = {};
                }
            }

            // Get active job info
            const filterStartMs = timeRange
                ? new Date(timeRange.start).getTime()
                : null;
            const filterEndMs = timeRange
                ? new Date(timeRange.end).getTime()
                : null;

            const activeJob = (currentDevice.jobSchedules || [])
                .filter((job) => {
                    if (!filterStartMs || !filterEndMs) return true;
                    if (!job.start_time || !job.end_time) return false;
                    const jobStartMs = new Date(job.start_time).getTime();
                    const jobEndMs = new Date(job.end_time).getTime();
                    if (isNaN(jobStartMs) || isNaN(jobEndMs)) return false;
                    return jobStartMs < filterEndMs && jobEndMs > filterStartMs;
                })
                .sort((a, b) => {
                    const aStart = new Date(a.start_time).getTime();
                    const bStart = new Date(b.start_time).getTime();
                    return bStart - aStart;
                })[0];

            const partName =
                activeJob?.part?.name || activeJob?.part_name || null;
            const isUnassignedPart = !partName;

            signalsToProcess.forEach((signal, signalIndex) => {
                const signalName = signal.name;

                // Skip status signals - they are for advocacy only, not production
                const typeFlags = getSignalTypeFromBits(signal.type, types);
                if (typeFlags?.status) {
                    console.log(
                        `ProductionReport - Skipping status signal: ${signalName}`,
                    );
                    return; // Skip this signal
                }

                const baseColor = getSignalColorFromDevice(
                    currentDevice,
                    signalName,
                    signalIndex,
                    defaultSignalColors,
                );

                const displayLabel = isUnassignedPart
                    ? `${signalName} `
                    : `${signalName} \n (${partName})`;

                // Get signal parameters for threshold and inversion
                const signalParams = params?.[signalName] || {};
                const minThreshold = signalParams?.minThreshold ?? 50;
                const maxThreshold = signalParams?.maxThreshold ?? 50;
                const isInverted = signalParams?.inverted || false;

                // Calculate threshold value: 50 = 512 (on a 0-1024 scale)
                const thresholdValue = (minThreshold / 100) * 1024;

                // Get datapoints for this signal from cachedDatapoints
                // Structure: cachedDatapoints.data[deviceName][signalName] = [datapoints]
                // OR cachedDatapoints[deviceName][signalName] = [datapoints]
                const deviceName = currentDevice.name || currentDevice.id;
                let deviceSignalsData = cachedDatapoints?.data?.[deviceName];
                if (!deviceSignalsData) {
                    // Fallback: maybe cachedDatapoints is already the data object
                    deviceSignalsData = cachedDatapoints?.[deviceName];
                }
                if (!deviceSignalsData) {
                    deviceSignalsData = {};
                }
                const allSignalDatapoints = deviceSignalsData[signalName] || [];

                console.log(
                    `ProductionReport - Signal "${signalName}": deviceName="${deviceName}", deviceSignalsData keys=${Object.keys(deviceSignalsData).join(",")}, has data=${!!deviceSignalsData[signalName]}, totalDatapoints=${allSignalDatapoints.length}`,
                );

                // Filter datapoints by time range
                const signalDatapoints = allSignalDatapoints
                    .filter((dp) => {
                        if (!timeRange) return true;
                        if (!dp.time) return false;
                        // Timestamps may be in seconds, convert to milliseconds
                        const dpTime =
                            typeof dp.time === "number"
                                ? dp.time * 1000
                                : new Date(dp.time).getTime();
                        return dpTime >= filterStartMs && dpTime <= filterEndMs;
                    })
                    .sort(
                        (a, b) =>
                            new Date(a.time).getTime() -
                            new Date(b.time).getTime(),
                    );

                console.log(
                    `ProductionReport - Signal "${signalName}": filteredDatapoints=${signalDatapoints.length}`,
                );

                // Detect transitions to count production units
                let productionCount = 0;
                if (signalDatapoints.length >= 2) {
                    // Check if signal is digital or analog
                    const isDigital = typeFlags?.digital === true;

                    // For digital: always count transitions
                    // For analog: only count if showThresholds is enabled
                    const shouldCountTransitions =
                        isDigital || localShowThresholds;

                    if (shouldCountTransitions) {
                        const transitions = detectTransitions(
                            signalDatapoints,
                            signal,
                            thresholdValue,
                            types,
                        );
                        productionCount = transitions.length;

                        console.log(
                            `ProductionReport - Signal "${signalName}": isDigital=${isDigital}, transitions=${productionCount}`,
                        );
                    } else {
                        console.log(
                            `ProductionReport - Signal "${signalName}": analog signal, thresholds OFF - skipping count`,
                        );
                    }

                    // Apply inverted logic if needed
                    if (isInverted) {
                        // For inverted signals, the transition count interpretation may differ
                        // but detectTransitions already handles the inversion
                        console.log(
                            `Signal "${signalName}" is inverted: transitions=${productionCount}`,
                        );
                    }
                } else if (signalDatapoints.length === 1) {
                    // Single datapoint, no transitions
                    console.log(
                        `ProductionReport - Signal "${signalName}": only 1 datapoint, no transitions`,
                    );
                    productionCount = 0;
                }

                data.push({
                    name: displayLabel,
                    units: productionCount,
                    signalName: signalName,
                    deviceId: currentDevice.id,
                    deviceName: currentDevice.name || currentDevice.id,
                    partName: partName,
                    isUnassigned: isUnassignedPart,
                });

                colors.push(getColorVariant(baseColor, 50));
            });

            return { data, colors };
        } catch (error) {
            console.error("Error in chartData useMemo:", error);
            return { data: [], colors: [] };
        }
    }, [currentDevice, types, timeRange, cachedDatapoints]);

    const handlePrevDevice = () => {
        onSelectedDeviceIndexChange((prev) => (prev > 0 ? prev - 1 : prev));
    };

    const handleNextDevice = () => {
        onSelectedDeviceIndexChange((prev) =>
            prev < deviceList.length - 1 ? prev + 1 : prev,
        );
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="tw-bg-white tw-p-2 tw-border tw-border-slate-200 tw-shadow-md tw-rounded tw-text-xs">
                    <p className="tw-font-semibold tw-mb-1">{data.name}</p>
                    <p style={{ color: payload[0].color }}>
                        Units: <strong>{data.units}</strong>
                    </p>
                </div>
            );
        }
        return null;
    };

    const CustomLabel = (props) => {
        const { x, y, width, height, value } = props;
        if (!value) return null;

        return (
            <text
                x={x + width - 10}
                y={y + height / 2}
                textAnchor="end"
                fill="#333"
                fontSize={12}
                dominantBaseline="middle"
            >
                {value}
            </text>
        );
    };

    return (
        <ReportResults
            deviceList={deviceList}
            selectedDeviceIndex={Math.min(
                selectedDeviceIndex,
                Math.max(0, deviceList.length - 1),
            )}
            onPrevDevice={handlePrevDevice}
            onNextDevice={handleNextDevice}
            hideEmptyDevices={hideEmptyDevices}
            onHideEmptyDevicesChange={(checked) => {
                setHideEmptyDevices(checked);
                onSelectedDeviceIndexChange(0);
            }}
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            showThresholds={localShowThresholds}
            onShowThresholdsChange={(value) => {
                setLocalShowThresholds(value);
                if (onShowThresholdsChange) onShowThresholdsChange(value);
            }}
            loading={loading}
            onRefresh={handleRefresh}
            onFavoriteClick={onFavoriteClick}
            onPrintClick={() => printReports(printRef)}
            onDownloadCSV={handleDownloadCSV}
            printRef={printRef}
            isAIAnalysisOpen={showAiPanel}
            isAIAnalyzing={aiAnalyzing}
            aiResults={aiResults}
            aiError={aiError}
            onCloseAIAnalysis={() => {
                setShowAiPanel(false);
                setAiResults(null);
                setAiError(null);
            }}
            onAIFollowUp={(question) => {
                console.log("Follow-up question:", question);
            }}
        >
            {/* Bar Chart */}
            {loading ? (
                <div className="report-loading-container">
                    <span className="report-loading-text">Loading...</span>
                </div>
            ) : chartData.data.length > 0 ? (
                <div className="tw-flex tw-flex-col tw-gap-4 tw-h-full tw-w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData.data} layout="vertical">
                            <CartesianGrid
                                strokeDasharray="3 3"
                                horizontal={true}
                                vertical={false}
                                stroke="#e0e0e0"
                            />
                            <XAxis
                                type="number"
                                tick={{ fontSize: 12 }}
                                domain={[0, 100]}
                                tickFormatter={(value) => value.toString()}
                            />
                            <YAxis
                                dataKey="name"
                                type="category"
                                tick={{ fontSize: 11 }}
                                width={190}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar
                                dataKey="units"
                                fill="#3b82f6"
                                label={CustomLabel}
                            >
                                {chartData.data.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={chartData.colors[index]}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <div className="report-loading-container">
                    <span className="report-loading-text">
                        No data available for selected device
                    </span>
                </div>
            )}
        </ReportResults>
    );
}
